import { createSelector } from "reselect"

import { selectUserId } from "@selectors/user"

const selectNotification = state => state.notification

const selectRawNotifications = createSelector(
  selectNotification,
  notification => notification.notifications
)

// ORG-NOTIF (QJ, 2026-07-04): "I do not and should not be notified about an ACTION I TAKE, or a
// Mention I make of myself." The dev backend already excludes the actor at notification CREATION
// (api/services/push.py create_notifications/notify, default exclude_actor=True; see also
// messages.py's explicit self-mention filter). This is the client-side belt-and-braces guard --
// it never lets a row whose actor is the signed-in user render in the bell/mentions list, even if
// a legacy row or a future backend call site regresses the server-side exclusion.
export const selectNotifications = createSelector(
  selectRawNotifications,
  selectUserId,
  (notifications, userId) =>
    (notifications || []).filter(
      n => !n || n.actorId == null || !userId || String(n.actorId) !== String(userId)
    )
)

export const selectOpenedFromNotification = createSelector(
  selectNotification,
  notification => notification.openedFromNotification
)

export const selectNotificationsLoading = createSelector(
  selectNotification,
  notification => notification.loading
)

export const selectLastSeenTimestamp = createSelector(
  selectNotification,
  notification => notification.lastSeenTimestamp
)

// Wave 3 NC1: notification-center unread badge count.
export const selectUnreadCount = createSelector(
  selectNotification,
  notification => notification.unreadCount || 0
)

// wave4-notif-conform: lazy-load pagination -- is there more history to fetch.
export const selectNotificationsHasMore = createSelector(
  selectNotification,
  notification => !!notification.hasMore
)

// wave4-notif-conform: { categoryId: unreadCount } for the category rail badges.
export const selectCategoryCounts = createSelector(
  selectNotification,
  notification => notification.categoryCounts || {}
)

// Wave 3 NC2: set of aliased teeup ids that have at least one UNREAD notification -- drives the
// inbox "activity since last view" indicator on each TeeupItem row.
export const selectUnreadTeeupIds = createSelector(
  selectNotifications,
  notifications =>
    new Set(
      (notifications || [])
        .filter(n => n && n.read === false && n.teeupId != null)
        .map(n => n.teeupId)
    )
)
